/**
 * 表示用フォーマッタ純関数群。
 */
import { type Timestamp, timestampDate } from '@bufbuild/protobuf/wkt'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

import { formatJstDateTimeHm } from '~/models/dateTime'

type CivilDate = { year: number; month: number; day: number }

/**
 * google.type.Decimal を表示・計算用の小数文字列に変換する。
 * 表示境界でのみ文字列化し、 float 変換は parseFloat 側で行う。
 * 未設定 (proto3 message は本質 nullable) は空文字を返す。
 */
export function decimalString(d: Decimal | undefined): string {
  return d?.value ?? ''
}

/**
 * 受渡期間を「yyyy/MM/dd〜yyyy/MM/dd」形式でフォーマット。
 * AC-002 / spec TC-D-18.1 準拠。
 */
export function formatDeliveryPeriod(from: CivilDate, to: CivilDate): string {
  return `${formatCivilDate(from)}〜${formatCivilDate(to)}`
}

/**
 * 受渡期間 (= 両端が undefined を許容) を「yyyy/MM/dd〜yyyy/MM/dd」 でフォーマット。
 * 片側 / 両側未設定なら「—」。 テーブル行表示で使う。
 */
export function formatPeriodRange(
  from: CivilDate | undefined,
  to: CivilDate | undefined,
): string {
  if (from === undefined && to === undefined) return '—'
  const fromStr = from !== undefined ? formatCivilDate(from) : '—'
  const toStr = to !== undefined ? formatCivilDate(to) : '—'
  return `${fromStr}〜${toStr}`
}

/**
 * 約定日を「yyyy/MM/dd」形式でフォーマット。
 * spec TC-D-19.1 準拠。
 */
export function formatExecutionDate(date: CivilDate): string {
  return formatCivilDate(date)
}

/**
 * 受渡期間の対象日数 (両端含む) を返す。 開始日 > 終了日 や未設定は undefined。
 * 手入力取引閲覧モーダル (Feature D) の「期間日数」 表示に使う。
 */
export function periodDaysInclusive(
  from: CivilDate | undefined,
  to: CivilDate | undefined,
): number | undefined {
  if (from === undefined || to === undefined) return undefined
  const fromMs = Date.UTC(from.year, from.month - 1, from.day)
  const toMs = Date.UTC(to.year, to.month - 1, to.day)
  if (toMs < fromMs) return undefined
  const dayMs = 24 * 60 * 60 * 1000
  return Math.round((toMs - fromMs) / dayMs) + 1
}

// ---------------------------------------------------------------------------
// internal helpers
// ---------------------------------------------------------------------------

function padTwoDigits(n: number): string {
  return n.toString().padStart(2, '0')
}

function formatCivilDate(date: CivilDate): string {
  return `${date.year}/${padTwoDigits(date.month)}/${padTwoDigits(date.day)}`
}

/**
 * 取り込み途絶バナーの最終取込 (AC-SBL-016)。JST 壁時計の `YYYY/MM/DD HH:mm`
 * (日本語)、`YYYY-MM-DD HH:mm` (英語)。未設定 (途絶えている連携元に一度も成功して
 * いないものを含む) は「—」。
 */
export function formatLastImportedAt(
  lastImportedAt: Timestamp | undefined,
  isEnglish: boolean,
): string {
  if (lastImportedAt === undefined) return '—'
  const formatted = formatJstDateTimeHm(timestampDate(lastImportedAt))
  return isEnglish ? formatted.replaceAll('/', '-') : formatted
}

/**
 * 取引先を「名称 (取引先コード)」で表示する文字列を組む (issue #7045)。
 *
 * 一覧の「取引先」列・手入力取引閲覧モーダル・Recap の 3 箇所が同じ規則で
 * 表示することが spec の要求 (`bilateral-trade-list.md` 表示データ「取引先」/
 * `bilateral-trade-manual-detail.md` 表示項目 / `bilateral-trade-recap.md`
 * 詳細テーブル) なので、組み立てをここ 1 箇所に置く。
 *
 * - 取引先コードが未登録 (空) なら名称のみ。
 * - 名称が空 (取引所を介した受渡で相手方を特定しない取引) の時は空のまま返す。
 *   併記は「名称にコードを添える」規則で、コードだけを名称の位置に出す規則は
 *   spec に無い。空の時の表示 (空欄 / 「—」) は呼び出し側の既存規則に任せる。
 * - spec の例は全角括弧「〇〇電力（OWN003）」だが、表記は `conventions.md`
 *   「かっこは半角に統一し、直前に半角スペース」に揃える (画面固有 spec より
 *   規約を優先。同じモーダルの自社側事業者コードと同じ扱い)。
 */
export function formatPartnerWithCode(name: string, code: string): string {
  return name === '' || code === '' ? name : `${name} (${code})`
}
